/**
 * useTransactions Hook
 * Fetches and manages transaction data for any inventory type
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../../../supabaseClient';
import { getInventoryConfig } from '../shared/inventoryConfig';

/**
 * Hook to fetch transactions with filtering support
 * @param {string} inventoryType - bottles, caps, labels, supplies
 * @param {string} companyId - Company UUID
 * @returns {object} transactions, loading, error, refresh, timeFormat, updateTimeFormat
 */
export default function useTransactions(inventoryType, companyId) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFormat, setTimeFormat] = useState('12h');

  const config = useMemo(() => {
    try {
      return getInventoryConfig(inventoryType);
    } catch {
      return null;
    }
  }, [inventoryType]);

  // Fetch user time format preference
  useEffect(() => {
    const fetchTimeFormat = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('time_format')
          .eq('id', user.id)
          .single();

        if (!profileError && profile?.time_format) {
          setTimeFormat(profile.time_format);
        }
      } catch (err) {
        console.error('Error fetching time format:', err);
      }
    };
    fetchTimeFormat();
  }, []);

  const updateTimeFormat = useCallback(async (newFormat) => {
    setTimeFormat(newFormat);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('profiles')
        .update({ time_format: newFormat })
        .eq('id', user.id);
    } catch (err) {
      console.error('Error updating time format:', err);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    if (!config || !companyId) return;

    try {
      setLoading(true);
      setError(null);

      // Build the select query with joins based on config
      // The item table foreign key is auto-joined by Supabase via the itemIdField
      const itemRelation = `${config.tableName}:${config.itemIdField}`;

      const itemFields = config.hasUnitOfMeasure
        ? 'id, name, sku, primary_unit_type, primary_unit_measure, primary_unit_quantity'
        : 'id, name, sku';

      const { data, error: fetchError } = await supabase
        .from(config.transactionsTable)
        .select(`
          *,
          ${itemRelation} (
            ${itemFields}
          ),
          location:location_id (
            id, name
          ),
          from_location:from_location_id (
            id, name
          ),
          to_location:to_location_id (
            id, name
          ),
          supplier:supplier_id (
            id, name
          )
        `)
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      if (fetchError) throw fetchError;

      // Normalize chemical transactions: add quantity alias from unit_quantity
      // so existing display components (TransactionsList, ViewTransactionDialogue) work
      const normalized = (data || []).map(tx => {
        if (!config.hasUnitOfMeasure) return tx;
        const item = tx[config.tableName];
        // Compute fallback total_quantity for older transactions where it may be null
        const fallbackTotal = tx.total_quantity == null && tx.unit_quantity != null && item?.primary_unit_quantity
          ? tx.unit_quantity * parseFloat(item.primary_unit_quantity)
          : null;
        return {
          ...tx,
          quantity:       tx.unit_quantity,                      // alias for display
          total_quantity: tx.total_quantity ?? fallbackTotal     // computed if missing
        };
      });

      setTransactions(normalized);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [config, companyId]);

  useEffect(() => {
    if (companyId && config) {
      fetchTransactions();
    }
  }, [companyId, config, fetchTransactions]);

  return {
    transactions,
    loading,
    error,
    refresh: fetchTransactions,
    timeFormat,
    updateTimeFormat,
    config
  };
}
